import * as authActions from '../actions/auth.actions';

import { Actions, ofType } from '@ngrx/effects';
import { DestroyRef, Injectable, inject } from '@angular/core';
import {
  ItemsProvider,
  PagedResult,
} from 'src/app/shared/components/dropdown-checkbox/dropdown-checkbox.component';
import { NomenclatureItem } from '../models/core.models';
import { Observable, catchError, map, of, tap } from 'rxjs';

import { FormOption } from './forms.service';
import { NomenclatureApiService } from 'src/app/api/services/nomenclature-api.service';
import { UtilsApiService } from 'src/app/api/services/utils-api.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface AutoCompleteResult {
  raw_value: any;
  text: string;
}

/**
 * Nomenclature client-side layer: per-session caches over the lookup lists the
 * forms use, plus their `FormOption` reshaping. All backend transport lives in
 * `NomenclatureApiService` / `UtilsApiService`.
 */
@Injectable({
  providedIn: 'root',
})
export class NomenclatureService {
  /**
   * Cache for storing nomenclature items.
   */
  private cacheNom: { [nomenclature: string]: NomenclatureItem[] } = {};

  private destroyRef = inject(DestroyRef);
  private nomenclatureApi = inject(NomenclatureApiService);
  private utilsApi = inject(UtilsApiService);

  constructor(private actions$: Actions) {
    // subscribe for global context-changing events to clear the static caches stored in the service
    this.actions$
      .pipe(
        ofType(authActions.logout, authActions.switchUserCompany),
        takeUntilDestroyed(this.destroyRef),
      )

      .subscribe(() => this.clearCaches());
  }

  fetchAutocompleteResults(
    searchTerm: string,
    propertyUri: string,
  ): Observable<Array<any>> {
    return this.nomenclatureApi
      .getAutocompleteResults(searchTerm, propertyUri)
      .pipe(
        map((response) =>
          response.map((item) => {
            return {
              id: item.raw_value,
              name: item.text,
            };
          }),
        ),
        catchError((error) => {
          console.error(error);
          return of([]); // Return empty array on error
        }),
      );
  }

  /**
   * Fetches the timezones from the server with cache support.
   * @returns An Observable that emits an array of NomenclatureItem.
   */
  fetchTimezones(): Observable<Array<NomenclatureItem>> {
    const cacheKey = 'timezones';
    if (this.cacheNom[cacheKey]) {
      return of(this.cacheNom[cacheKey]);
    }

    return this.utilsApi
      .getTimezones()
      .pipe(tap((response) => (this.cacheNom[cacheKey] = response)));
  }

  fetchPasswordComplexities(): Observable<Array<NomenclatureItem>> {
    return this.fetchNomenclatureRecords('password_complexity');
  }

  /**
   * Fetches nomenclature records based on the provided nomenclature.
   * If the records are available in the cache, it returns them immediately.
   * Otherwise, it makes an HTTP request to retrieve the records and updates the cache.
   *
   * @param nomenclature The name of the nomenclature to fetch records for.
   * @returns An Observable that emits an array of NomenclatureItem objects.
   */
  fetchNomenclatureRecords(
    nomenclature: string,
    transform: boolean = true,
  ): Observable<NomenclatureItem[] | any> {
    // Check cache first. The cache holds the TRANSFORMED {id, title} records,
    // so it only answers transformed requests: a raw request (transform =
    // false, e.g. a nomenclature column's rawRecord labelling) must reach the
    // backend even when the same nomenclature was already fetched transformed
    // - otherwise the raw mapper sees {id, title} and labels come out as
    // "<id> - undefined" (WAP-1658).
    if (transform && this.cacheNom[nomenclature]) {
      return of(this.cacheNom[nomenclature]);
    }

    return this.nomenclatureApi.getNomenclatureRecordsRaw(nomenclature).pipe(
      map((response) => {
        if (!transform) {
          return response;
        }
        // Transform the response object into an array
        const transformed = Object.values(response).map((item) => ({
          id: item.id,
          title: (item as any).name,
        }));

        // Update cache
        this.cacheNom[nomenclature] = transformed;

        return transformed;
      }),
    );
  }

  clearCaches() {
    // console.log('nomenclature-service: clear caches');
    this.cacheNom = {};
  }

  /**
   * Fetches the list of languages with cache support.
   * @param supportedOnly Specifies whether to fetch only supported languages.
   * @returns An observable that emits an array of NomenclatureItem objects representing the languages.
   */
  fetchLanguages(
    supportedOnly: boolean = true,
  ): Observable<Array<NomenclatureItem>> {
    const cacheKey = `languages_${supportedOnly}`;
    if (this.cacheNom[cacheKey]) {
      return of(this.cacheNom[cacheKey]);
    }

    return this.utilsApi
      .getLanguages(supportedOnly)
      .pipe(tap((response) => (this.cacheNom[cacheKey] = response)));
  }

  /**
   * Retrieves the schedule coverage periods from the server with cache support.
   * @returns An Observable that emits an array of NomenclatureItem objects.
   */
  fetchScheduleCoveragePeriods(): Observable<Array<any>> {
    const cacheKey = 'schedule_coverage_periods';
    if (this.cacheNom[cacheKey]) {
      return of(this.cacheNom[cacheKey]);
    }

    return this.utilsApi
      .getScheduleCoveragePeriods()
      .pipe(tap((response) => (this.cacheNom[cacheKey] = response)));
  }

  /**
   * Fetches the company profit centers with cache support.
   * @returns An Observable that emits an array of profit centers.
   */
  fetchCompanyProfitCenters(): Observable<Array<any>> {
    const cacheKey = 'profit_centers';
    if (this.cacheNom[cacheKey]) {
      return of(this.cacheNom[cacheKey]);
    }

    return this.utilsApi
      .getCompanyProfitCenters()
      .pipe(tap((response) => (this.cacheNom[cacheKey] = response)));
  }

  /**
   * Fetches the company partner agencies with cache support.
   * @returns An Observable that emits an array of partner agencies.
   */
  fetchCompanyPartnerAgencies(): Observable<Array<any>> {
    const cacheKey = 'partner_agencies';
    if (this.cacheNom[cacheKey]) {
      return of(this.cacheNom[cacheKey]);
    }

    return this.utilsApi
      .getCompanyPartnerAgencies()
      .pipe(tap((response) => (this.cacheNom[cacheKey] = response)));
  }

  /**
   * Fetches the list of countries with cache support.
   * @returns An Observable that emits an array of country objects.
   */
  fetchCountries(): Observable<Array<any>> {
    const cacheKey = 'countries';
    if (this.cacheNom[cacheKey]) {
      return of(this.cacheNom[cacheKey]);
    }

    return this.utilsApi
      .getCountries()
      .pipe(tap((response) => (this.cacheNom[cacheKey] = response)));
  }

  fetchCitiesByCountryId(countryId: number): Observable<Array<any>> {
    const cacheKey = `cities_country_${countryId}`;
    if (this.cacheNom[cacheKey]) {
      return of(this.cacheNom[cacheKey]);
    }

    return this.utilsApi
      .getCitiesByCountryId(countryId)
      .pipe(tap((response) => (this.cacheNom[cacheKey] = response)));
  }

  /**
   * Fetches the company account types from the nomenclature records.
   * @returns An Observable that emits an array of company account types.
   */
  fetchCompanyAccountTypes(): Observable<Array<any>> {
    return this.fetchNomenclatureRecords('company_account_type');
  }

  fetchCompanyAccountStatuses(): Observable<Array<any>> {
    return this.fetchNomenclatureRecords('company_account_status');
  }

  fetchBusinessEntityTypes(): Observable<Array<any>> {
    return this.fetchNomenclatureRecords('business_entity_type');
  }

  fetchBusinessIndustries(): Observable<Array<any>> {
    return this.fetchNomenclatureRecords('business_industry');
  }
  fetchBusinessSectors(): Observable<Array<any>> {
    return this.fetchNomenclatureRecords('business_sector');
  }

  fetchCompanyMarkets(): Observable<Array<any>> {
    return this.fetchNomenclatureRecords('market');
  }

  fetchEntityRegistrationIdTypes(): Observable<Array<any>> {
    return this.fetchNomenclatureRecords('entity_reg_id_type');
  }
  /**
   * Fetches the company client types from the nomenclature records.
   * @returns An Observable that emits an array of company client types.
   */
  fetchCompanyRelationshipTypes(): Observable<Array<any>> {
    return this.fetchNomenclatureRecords('company_relationship_type');
  }

  fetchUserAccessLevels(): Observable<Array<any>> {
    return this.fetchNomenclatureRecords('user_access_level');
  }

  fetchOutputFormats(): Observable<Array<any>> {
    return this.fetchNomenclatureRecords('output_format');
  }

  fetchMattermostChannels(serverId: number): Observable<Array<any>> {
    // Generate a unique cache key for the given server ID.
    const cacheKey = `mattermost_channels_${serverId}`;

    // Return the cached response if it exists.
    if (this.cacheNom[cacheKey]) {
      return of(this.cacheNom[cacheKey]);
    }

    return this.nomenclatureApi
      .getMattermostChannels(serverId)
      .pipe(tap((response) => (this.cacheNom[cacheKey] = response)));
  }
  fetchMattermostServers(): Observable<Array<NomenclatureItem>> {
    const cacheKey = `mattermost_servers`;

    // Return the cached response if it exists.
    if (this.cacheNom[cacheKey]) {
      return of(this.cacheNom[cacheKey]);
    }

    return this.nomenclatureApi
      .getMattermostServers()
      .pipe(tap((response) => (this.cacheNom[cacheKey] = response)));
  }

  // Factory that creates an ItemsProvider for a given nomenclature
  makeNomenclatureItemsProvider(nomenclature: string): ItemsProvider {
    return (term: string, opts?: { pageToken?: string; limit?: number }) => {
      return this.nomenclatureApi
        .getNomenclatureAutocomplete(nomenclature, term, opts)
        .pipe(
          map((resp) => {
            const body = resp.body;

            const rawItems: any[] = Array.isArray(body?.items)
              ? body.items
              : Object.values(body ?? {});

            const items: FormOption[] = rawItems.map((item) => ({
              value: item.id,
              name: item.name,
              image: item.image,
            }));

            const nextPageToken =
              body?.next_page_token ??
              body?.next_token ??
              resp.headers.get('x-next-page-token') ??
              undefined;

            return {
              items,
              next_page_token: nextPageToken,
            } as PagedResult<FormOption>;
          }),
        );
    };
  }

  /**
   * Fetches nomenclature items by their IDs and transforms them into `FormOption` format.
   *
   * @param nomenclature - The name of the nomenclature to query (e.g., "country", "language").
   * @param ids - An array of numeric IDs to retrieve corresponding nomenclature items for.
   * @returns An Observable emitting an array of `FormOption` objects.
   */
  fetchNomencatureItemsByIdsAsFormOptions(
    nomenclature: string,
    ids: number[],
  ): Observable<FormOption[]> {
    return this.nomenclatureApi
      .getNomenclatureItemsByIds(nomenclature, ids)
      .pipe(
        map((response) => {
          const transformed = Object.values(response).map((item) => ({
            value: item.id,
            name: (item as any).name,
          }));

          return transformed;
        }),
      );
  }

  fetchLocation(locationId: number): Observable<FormOption | null> {
    return this.nomenclatureApi.getLocation(locationId).pipe(
      map((response) =>
        response && response.id != null
          ? { value: response.id, name: response.name }
          : null,
      ),
    );
  }
}
